import { type FC } from "react";
import {
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import copy from "copy-to-clipboard";
import type { StreamEntrypoint } from "@/partner/entities/streamEntrypoints";

interface StreamEntrypointAddressesProps {
  entrypoint: StreamEntrypoint;
}

// Разметка/подход скопированы из StreamBroadcastTab.tsx (streams-фича) —
// тот же набор read-only полей с копированием (RTMPS URL+ключ, SRT
// URL+streamId+passphrase). Лейблы/тултип/снекбар копирования намеренно
// переиспользуют существующие ключи streams.form.* (streamKeyField,
// srtStreamIdField, srtPassphraseField, copyTooltip, copied) — тот же
// смысл, дублировать параллельный набор под streamEntrypoints.* незачем.
// Playback-адреса того же live input (забор сигнала по SRT или RTMPS) живут в
// отдельном компоненте StreamEntrypointPlaybackAddresses: в форме они
// показываются своей карточкой справа. У старых точек поля null, пока
// бэкенд их не дописал.
function useCopyField() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const handleCopy = (value: string) => {
    copy(value);
    enqueueSnackbar(t("streams.form.copied"), { variant: "success" });
  };

  return (label: string, value: string) => (
    <Grid size={12} key={label + value}>
      <TextField
        fullWidth
        variant="outlined"
        label={label}
        value={value}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <Tooltip title={t("streams.form.copyTooltip")}>
              <IconButton size="small" onClick={() => handleCopy(value)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ),
        }}
      />
    </Grid>
  );
}

export const StreamEntrypointAddresses: FC<StreamEntrypointAddressesProps> = ({
  entrypoint,
}) => {
  const { t } = useTranslation();
  const renderCopyField = useCopyField();

  return (
    <Grid container spacing={2}>
      {renderCopyField("RTMPS URL", entrypoint.rtmpsUrl)}
      {renderCopyField(
        t("streams.form.streamKeyField"),
        entrypoint.rtmpsStreamKey
      )}
      {renderCopyField("SRT URL", entrypoint.srtUrl)}
      {renderCopyField(
        t("streams.form.srtStreamIdField"),
        entrypoint.srtStreamId
      )}
      {renderCopyField(
        t("streams.form.srtPassphraseField"),
        entrypoint.srtPassphrase
      )}
    </Grid>
  );
};

export const StreamEntrypointPlaybackAddresses: FC<
  StreamEntrypointAddressesProps
> = ({ entrypoint }) => {
  const { t } = useTranslation();
  const renderCopyField = useCopyField();

  const ready =
    entrypoint.rtmpsPlaybackUrl &&
    entrypoint.rtmpsPlaybackStreamKey &&
    entrypoint.srtPlaybackUrl &&
    entrypoint.srtPlaybackStreamId &&
    entrypoint.srtPlaybackPassphrase;

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Typography variant="body2" color="text.secondary">
          {t("streams.form.playbackBlockHint")}
        </Typography>
      </Grid>
      {ready ? (
        <>
          {renderCopyField(
            t("streams.form.rtmpsPlaybackUrlField"),
            entrypoint.rtmpsPlaybackUrl as string
          )}
          {renderCopyField(
            t("streams.form.streamKeyField"),
            entrypoint.rtmpsPlaybackStreamKey as string
          )}
          {renderCopyField(
            t("streams.form.srtPlaybackUrlField"),
            entrypoint.srtPlaybackUrl as string
          )}
          {renderCopyField(
            t("streams.form.srtStreamIdField"),
            entrypoint.srtPlaybackStreamId as string
          )}
          {renderCopyField(
            t("streams.form.srtPassphraseField"),
            entrypoint.srtPlaybackPassphrase as string
          )}
        </>
      ) : (
        <Grid size={12}>
          <Typography variant="body2" color="text.secondary">
            {t("streams.form.playbackUnavailable")}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};
